// ─── Role configuration — single source of truth ─────────────────
// Every UI decision about what a role sees comes from here.

export const ROLES = {
  super_admin: {
    label: 'Super Admin',
    color: '#7C3AED',
    bg:    '#F5F3FF',
    dashboardType: 'admin',
    greeting: 'Full system access',
  },
  admin: {
    label: 'Admin',
    color: '#403EC9',
    bg:    '#EEEDFE',
    dashboardType: 'admin',
    greeting: 'Platform administration',
  },
  ceo: {
    label: 'CEO',
    color: '#0369A1',
    bg:    '#E0F2FE',
    dashboardType: 'executive',
    greeting: 'Executive overview',
  },
  director: {
    label: 'Director',
    color: '#0F766E',
    bg:    '#CCFBF1',
    dashboardType: 'executive',
    greeting: 'Director overview',
  },
  vp_sales: {
    label: 'VP Sales',
    color: '#15803D',
    bg:    '#DCFCE7',
    dashboardType: 'vp',
    greeting: 'Sales performance overview',
  },
  rsm: {
    label: 'RSM',
    color: '#B45309',
    bg:    '#FEF3C7',
    dashboardType: 'field',  // same as rep but sees team
    greeting: 'Regional overview',
  },
  sales_rep: {
    label: 'Sales Rep',
    color: '#B91C1C',
    bg:    '#FEE2E2',
    dashboardType: 'field',
    greeting: "Today's field plan",
  },
  bde: {
    label: 'BDE',
    color: '#6B7280',
    bg:    '#F9FAFB',
    dashboardType: 'field',
    greeting: 'Your accounts',
  },
  employee: {
    label: 'Employee',
    color: '#0369A1',
    bg:    '#E0F2FE',
    dashboardType: 'employee',
    greeting: 'Your day',
  },
  distributor: {
    label: 'Distributor',
    color: '#0F766E',
    bg:    '#CCFBF1',
    dashboardType: 'distributor',
    greeting: 'Distributor portal',
  },
  finance: {
    label: 'Finance',
    color: '#0369A1',
    bg:    '#E0F2FE',
    dashboardType: 'vp',
    greeting: 'Sales & finance overview',
  },
  dispatch: {
    label: 'Dispatch',
    color: '#D97706',
    bg:    '#FEF3C7',
    dashboardType: 'dispatch',
    greeting: 'Warehouse & Dispatch',
  },
  investor: {
    label: 'Investor',
    color: '#403EC9',
    bg:    '#EEF2FF',
    dashboardType: 'investor',
    greeting: 'Business Overview',
  },
  engineer: {
    label: 'Engineer',
    color: '#0e5c7a',
    bg:    '#E4F0F4',
    dashboardType: 'firmware',
    greeting: 'Firmware Registry',
  },
  qa: {
    label: 'QA',
    color: '#8a5606',
    bg:    '#FBF0D9',
    dashboardType: 'firmware',
    greeting: 'Firmware Registry',
  },
  ra_approver: {
    label: 'RA Approver',
    color: '#0f6e56',
    bg:    '#E0F2EC',
    dashboardType: 'firmware',
    greeting: 'Firmware Registry',
  },
};

// Which dashboard to show
export const getDashboardType = (roleSlug) => ROLES[roleSlug]?.dashboardType || 'field';

// Sidebar nav items per role group
export const NAV_CONFIG = {
  // Admin-type roles see everything
  admin: ['dashboard','doctors','accounts','expansion','targets','actuals','visits','travel','events','territories','users','roles'],
  // Executive roles — read-only strategic view
  executive: ['dashboard','doctors','accounts','targets','actuals','visits','travel','events','territories'],
  // VP Sales — can edit targets, approve actuals, see all
  vp: ['dashboard','doctors','accounts','expansion','targets','actuals','visits','travel','events','territories'],
  // RSM — own territory, can approve team actuals
  rsm: ['dashboard','doctors','accounts','expansion','targets','actuals','visits','travel','events'],
  // Field roles — personal view, can log
  field: ['dashboard','doctors','accounts','visits','actuals','travel','events'],
};

export function getNavForRole(roleSlug) {
  const type = getDashboardType(roleSlug);
  if (roleSlug === 'super_admin' || roleSlug === 'admin') return NAV_CONFIG.admin;
  if (roleSlug === 'ceo' || roleSlug === 'director') return NAV_CONFIG.executive;
  if (roleSlug === 'vp_sales') return NAV_CONFIG.vp;
  if (roleSlug === 'rsm') return NAV_CONFIG.rsm;
  return NAV_CONFIG.field;
}

// Quick checks
export const isFieldRole   = (slug) => ['rsm','sales_rep','bde'].includes(slug);
export const isExecRole    = (slug) => ['ceo','director'].includes(slug);
export const isAdminRole   = (slug) => ['super_admin','admin'].includes(slug);
export const canApprove    = (slug) => ['super_admin','admin','vp_sales','rsm'].includes(slug);
export const canLogVisit   = (slug) => ['super_admin','admin','rsm','sales_rep','bde'].includes(slug);
export const canLogSale    = (slug) => ['super_admin','admin','rsm','sales_rep'].includes(slug);
export const canEditTarget = (slug) => ['super_admin','admin','vp_sales'].includes(slug);
export const seeOwnOnly    = (slug) => ['sales_rep','bde'].includes(slug);
export const seeTeam       = (slug) => ['rsm'].includes(slug); // RSM sees team but not all territories
